import { useMediaQuery } from '@mui/material';
import { isToday } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../constants';
import { ProductDetails, SoldProductItem, useAsyncAction, useDropdownItems, useSoldProducts } from '../../hooks';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { Typography } from '../../stories/atoms';
import { ColumnFlexContainer, ContainerV2, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, DropdownMenu, FilterTag, FilterTagsWidget, Search } from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { homeTranslations, soldProductsTranslations, transactionSearchTranslations } from '../../translations';
import { getDatePickerLabel, isFailureResponse } from '../../utils';
import { HomeDatePicker } from '../home/HomeDatePicker';
import { ProductTable } from './productTable/ProductTable';
import { ProductDetailsSlider } from './slider/ProductDetailsSlider';
import { SoldProductsMobileHeader } from './soldProductsMobileHeader/SoldProductsMobileHeader';
import { filterProductsBySearch, FilterType, getIsItemFilterEnabled } from './utils';

const { clearFilter: clearFilterTranslations } = transactionSearchTranslations;
const { datePicker } = homeTranslations;

export const SoldProductsPage: FC = observer(() => {
  usePageEventTracker('sold_products');

  const { t } = useTranslation();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const { fetchSoldProducts, fetchSoldProductsDetails } = useSoldProducts();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentInterval, setCurrentInterval] = useState<StorePerformanceInterval>(StorePerformanceInterval.DAILY);
  const [filteredDepartmentNames, setFilteredDepartmentNames] = useState<string[]>([]);
  const [filteredIndexes, setFilteredIndexes] = useState<string[]>([]);
  const [filteredSubIndexes, setFilteredSubIndexes] = useState<string[]>([]);
  const [selectedSearchProduct, setSelectedSearchProduct] = useState<SoldProductItem | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [dateLabel, setDateLabel] = useState(
    getDatePickerLabel(
      selectedDate,
      currentInterval,
      t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
    ),
  );

  const {
    result: soldProductItems,
    isLoading: soldProductItemsLoading,
    isError: soldProductItemsError,
    triggerAction: triggerGetSoldProductItems,
  } = useAsyncAction<SoldProductItem[]>();

  // TODO: Move to specific tab later
  const {
    result: soldProductDetails,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    triggerAction: triggerGetSoldProductDetails,
    reset: resetSoldProductDetails,
  } = useAsyncAction<ProductDetails>();

  const getSoldProducts = async () => {
    const request = createStorePerformanceRequest({
      interval: currentInterval,
      currentDate: selectedDate,
    });
    const response = await fetchSoldProducts(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const getSoldProductDetails = async (productId: string) => {
    const request = createStorePerformanceRequest({
      interval: currentInterval,
      currentDate: selectedDate,
    });
    const response = await fetchSoldProductsDetails(productId, request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  useEffect(() => {
    setSelectedProductId(null);
    triggerGetSoldProductItems(getSoldProducts);
    setDateLabel(
      getDatePickerLabel(
        selectedDate,
        currentInterval,
        t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
      ),
    );
  }, [selectedDate, currentInterval]);

  // Fetch product details when a product is selected and reset before fetching
  useEffect(() => {
    if (!selectedProductId) return;

    resetSoldProductDetails();

    triggerGetSoldProductDetails(() => getSoldProductDetails(selectedProductId));
  }, [selectedProductId]);

  // Filtering
  const filteredSoldProductItems = useMemo(() => {
    if (!soldProductItems) return [];

    // Sort products by sold pieces
    const sortedSoldProducts = [...soldProductItems].sort((a, b) => b.soldPieces - a.soldPieces);

    let filtered = sortedSoldProducts.filter((item) => {
      const inDept = filteredDepartmentNames.length === 0 || filteredDepartmentNames.includes(item.departmentName);
      const inIndex = filteredIndexes.length === 0 || filteredIndexes.includes(item.index);
      const inSub = filteredSubIndexes.length === 0 || filteredSubIndexes.includes(item.subIndex);
      return inDept && inIndex && inSub;
    });

    if (selectedSearchProduct) {
      filtered = filtered.filter((item) => item.productId === selectedSearchProduct.productId);
    }

    return filtered;
  }, [soldProductItems, filteredDepartmentNames, filteredIndexes, filteredSubIndexes, selectedSearchProduct]);

  useEffect(() => {
    if (selectedProductId && !filteredSoldProductItems.some((i) => i.productId === selectedProductId)) {
      setSelectedProductId(null);
    }
  }, [filteredSoldProductItems, selectedProductId]);

  const selectedSoldProductItem =
    filteredSoldProductItems.find((item) => item.productId === selectedProductId) || null;

  const isNoData = !soldProductItemsLoading && filteredSoldProductItems.length === 0;
  const isDateFilterActive = !isToday(selectedDate);
  const isAnyFilterSelected =
    filteredDepartmentNames.length > 0 ||
    filteredIndexes.length > 0 ||
    filteredSubIndexes.length > 0 ||
    selectedSearchProduct !== null ||
    isDateFilterActive;

  // Handlers
  const handleDateChange = (date: Date, interval: StorePerformanceInterval) => {
    setSelectedDate(date);
    setCurrentInterval(interval);
  };

  const handleClearAll = () => {
    setSelectedDate(new Date());
    setCurrentInterval(StorePerformanceInterval.DAILY);
    setFilteredDepartmentNames([]);
    setFilteredIndexes([]);
    setFilteredSubIndexes([]);
    setSelectedSearchProduct(null);
    setSelectedProductId(null);
  };

  const handleFilterChange = (type: FilterType, selectedKeys: string[]) => {
    if (type === 'department') setFilteredDepartmentNames(selectedKeys);
    if (type === 'index') setFilteredIndexes(selectedKeys);
    if (type === 'subIndex') setFilteredSubIndexes(selectedKeys);
    setSelectedProductId(null);
  };

  const getSearchOptionLabel = (p: SoldProductItem) => `${p.productName} - ${p.productId}`;

  const departmentDropdownItems = useDropdownItems({
    allItems: soldProductItems,
    getItemValue: (item) => item.departmentName,
    isItemEnabled: (item) =>
      getIsItemFilterEnabled(
        soldProductItems || [],
        'department',
        item.departmentName,
        filteredDepartmentNames,
        filteredIndexes,
        filteredSubIndexes,
      ),
  });

  const indexDropdownItems = useDropdownItems({
    allItems: soldProductItems,
    getItemValue: (item) => item.index,
    isItemEnabled: (item) =>
      getIsItemFilterEnabled(
        soldProductItems || [],
        'index',
        item.index,
        filteredDepartmentNames,
        filteredIndexes,
        filteredSubIndexes,
      ),
  });

  const subIndexDropdownItems = useDropdownItems({
    allItems: soldProductItems,
    getItemValue: (item) => item.subIndex,
    isItemEnabled: (item) =>
      getIsItemFilterEnabled(
        soldProductItems || [],
        'subIndex',
        item.subIndex,
        filteredDepartmentNames,
        filteredIndexes,
        filteredSubIndexes,
      ),
  });

  const departmentDropdownItemsJSX = departmentDropdownItems.map((d) => ({
    ...d,
    item: <Typography>{d.item.departmentName}</Typography>,
  }));

  const indexDropdownItemsJSX = indexDropdownItems.map((d) => ({
    ...d,
    item: <Typography>{d.item.index}</Typography>,
  }));

  const subIndexDropdownItemsJSX = subIndexDropdownItems.map((d) => ({
    ...d,
    item: <Typography>{d.item.subIndex}</Typography>,
  }));

  return (
    <Page
      header={
        <RowFlexContainer
          padding={isMobile ? [0, 5] : [0, 8]}
          justifyContent={isMobile ? 'between' : 'start'}
          gap={[5]}
          alignItems="center"
          height={'100%'}
        >
          <HomeDatePicker
            currentDate={selectedDate}
            dateLabel={dateLabel}
            onDateChange={handleDateChange}
            isDatePickerDisabled={soldProductItemsLoading}
            showMonthyPicker
          />

          {!isMobile &&
            (['department', 'index', 'subIndex'] as FilterType[]).map((type) => {
              const items =
                type === 'department'
                  ? departmentDropdownItemsJSX
                  : type === 'index'
                  ? indexDropdownItemsJSX
                  : subIndexDropdownItemsJSX;
              const selected =
                type === 'department'
                  ? filteredDepartmentNames
                  : type === 'index'
                  ? filteredIndexes
                  : filteredSubIndexes;

              return (
                <DropdownMenu
                  key={type}
                  dropdownLabel={t(
                    soldProductsTranslations.productDetailsView[type].key,
                    soldProductsTranslations.productDetailsView[type].defaultValue,
                  )}
                  menuPosition="left"
                  maxHeight="50vh"
                  menuItems={items}
                  checkbox
                  multiSelect
                  initialSelected={selected}
                  buttonContentProps={{ isDisabled: items.length === 0 }}
                  onSelect={(keys) => handleFilterChange(type, keys)}
                />
              );
            })}

          {isMobile && (
            <SoldProductsMobileHeader
              filteredItems={filteredSoldProductItems}
              departmentDropdownMenuItems={departmentDropdownItemsJSX}
              indexDropdownMenuItems={indexDropdownItemsJSX}
              subIndexDropdownMenuItems={subIndexDropdownItemsJSX}
              filteredDepartmentNames={filteredDepartmentNames}
              filteredIndexes={filteredIndexes}
              filteredSubIndexes={filteredSubIndexes}
              updateFilteredItems={(keys, type) => handleFilterChange(type, keys)}
              clearAllFilters={handleClearAll}
            />
          )}
        </RowFlexContainer>
      }
      body={
        <div>
          <ColumnFlexContainer data-testid="sub-header-wrapper" margin={isMobile ? [3, 5] : [3, 8]} gap={[3]}>
            <RowFlexContainer data-testid="search-count-wrapper" justifyContent="between" alignItems="center">
              <ContainerV2 width={isMobile ? '100%' : '350px'}>
                <Search
                  fieldLabel={t(
                    soldProductsTranslations.productSearch.key,
                    soldProductsTranslations.productSearch.defaultValue,
                  )}
                  options={filteredSoldProductItems || []}
                  getOptionLabel={getSearchOptionLabel}
                  onSelect={setSelectedSearchProduct}
                  filterFunction={filterProductsBySearch}
                  onClear={() => setSelectedSearchProduct(null)}
                  markerOption={selectedSearchProduct}
                  disabled={soldProductItemsLoading || !soldProductItems || soldProductItems.length === 0}
                  inputFieldSize="small"
                />
              </ContainerV2>
              {!isMobile && !soldProductItemsLoading && !soldProductItemsError && soldProductItems && (
                <Typography data-testid="product-count">
                  {`${filteredSoldProductItems.length} ${t(
                    soldProductsTranslations.products.key,
                    soldProductsTranslations.products.defaultValue,
                  )}`}
                </Typography>
              )}
            </RowFlexContainer>

            <FilterTagsWidget
              isClearButtonVisible={isAnyFilterSelected && !isMobile}
              clearButtonText={t(clearFilterTranslations.key, clearFilterTranslations.defaultValue)}
              clearButtonHandler={handleClearAll}
              filterTags={[
                <Typography key="product-based-on" padding={[2, 0]} type="body3">
                  {t(
                    soldProductsTranslations.productBasedOn.key,
                    soldProductsTranslations.productBasedOn.defaultValue,
                  )}
                </Typography>,
                isDateFilterActive && !isMobile ? (
                  <FilterTag
                    key={dateLabel}
                    title={dateLabel}
                    onClick={() => handleDateChange(new Date(), StorePerformanceInterval.DAILY)}
                  />
                ) : (
                  <Typography key="date-label" data-testid="date-label" padding={[2, 0]} type="body3">
                    {dateLabel}
                  </Typography>
                ),
                ...filteredDepartmentNames.map(
                  (d) =>
                    !isMobile && (
                      <FilterTag
                        key={`department-${d}`}
                        title={d}
                        onClick={() => setFilteredDepartmentNames(filteredDepartmentNames.filter((x) => x !== d))}
                      />
                    ),
                ),
                ...filteredIndexes.map(
                  (i) =>
                    !isMobile && (
                      <FilterTag
                        key={`index-${i}`}
                        title={i}
                        onClick={() => setFilteredIndexes(filteredIndexes.filter((x) => x !== i))}
                      />
                    ),
                ),
                ...filteredSubIndexes.map(
                  (s) =>
                    !isMobile && (
                      <FilterTag
                        key={`subIndex-${s}`}
                        title={s}
                        onClick={() => setFilteredSubIndexes(filteredSubIndexes.filter((x) => x !== s))}
                      />
                    ),
                ),
              ]}
            />
          </ColumnFlexContainer>

          {!soldProductItemsLoading && soldProductItemsError && (
            <Animation
              animation={Animations.SAD}
              title={t(
                soldProductsTranslations.error.errorTitle.key,
                soldProductsTranslations.error.errorTitle.defaultValue,
              )}
              subtitle={t(
                soldProductsTranslations.error.errorSubtitle.key,
                soldProductsTranslations.error.errorSubtitle.defaultValue,
              )}
            />
          )}

          {isNoData && !soldProductItemsLoading && !soldProductItemsError && (
            <Animation
              animation={Animations.CONFUSED}
              title={t(
                soldProductsTranslations.noData.title.key,
                soldProductsTranslations.noData.title.defaultValue,
              )}
              subtitle={t(
                soldProductsTranslations.noData.subtitle.key,
                soldProductsTranslations.noData.subtitle.defaultValue,
              )}
            />
          )}

          <RowFlexContainer padding={isMobile ? [0, 5] : [0, 8]} flex={1} minHeight={[0]}>
            <ProductTable
              isLoading={soldProductItemsLoading}
              items={filteredSoldProductItems}
              selectedItemProductId={selectedProductId}
              interval={currentInterval}
              currentDate={selectedDate}
              onItemSelect={(item) => setSelectedProductId(item?.productId || null)}
            />

            {selectedSoldProductItem && (
              <ContainerV2 overflow="hidden" width={[88]} height="100">
                <ProductDetailsSlider
                  isVisible
                  onClose={() => setSelectedProductId(null)}
                  product={selectedSoldProductItem}
                  productDetails={soldProductDetails}
                  isDetailsLoading={isDetailsLoading}
                  isDetailsError={isDetailsError}
                />
              </ContainerV2>
            )}
          </RowFlexContainer>
        </div>
      }
    />
  );
});
